import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { ClientFormDrawer } from '@/components/ClientForm/ClientFormDrawer';
import ContactDetails from '@/components/ContactDetails';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import { useUpdateRequestClient } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import useViewPort from '@/utils/useViewport';

import { ClientSearchInput } from './ClientSearchInput/ClientSearchInput';
import { AddClientModal } from './AddClientModal';

export const ClientManager = () => {
  const { t } = useLingui();
  const { isMobile, isTablet } = useViewPort();
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);

  const { request } = useRequestContext();

  const { mutateAsync: updateRequestClient } = useUpdateRequestClient(request.id);

  // Show client details if client has externalId (from CRM) OR has meaningful data (manually created)
  const hasClientData =
    request.client?.externalId ||
    request.client?.name ||
    request.client?.firstName ||
    request.client?.lastName ||
    request.client?.email;

  if (hasClientData && request.client) {
    if (isMobile || isTablet) {
      return (
        <>
          <Stack gap="1rem">
            <ContactDetails
              type="client"
              name={request.client.name}
              email={request.client.email}
              phone={request.client.phone}
              address={request.client.address}
              placeIcon={null}
              deliveryChoice={request.client.shippingChoice}
              locale={request.client.locale}
              isVIP={request.vip}
            />
            <Stack row gap="0.75rem" style={{ width: '100%' }}>
              <Button
                variant="secondary"
                size="medium"
                style={{ flex: 1 }}
                onPress={() =>
                  updateRequestClient({
                    client: null,
                  })
                }
                ariaLabel={t({
                  id: 'requests.client-manager.remove-client',
                  message: 'Remove client',
                })}
              >
                <IconCross />
                <Trans id="request.articles.table.column.actions.remove">Remove</Trans>
              </Button>
              <Button
                variant="secondary"
                size="medium"
                style={{ flex: 1 }}
                onPress={() => setIsEditDrawerOpen(true)}
                ariaLabel={t({
                  id: 'requests.client-manager.edit-client',
                  message: 'Edit client',
                })}
              >
                <IconEdit />
                <Trans id="actions-table.edit">Edit</Trans>
              </Button>
            </Stack>
          </Stack>
          <ClientFormDrawer
            isOpen={isEditDrawerOpen}
            setIsOpen={setIsEditDrawerOpen}
            hideDeliveryOption
            customTitle={<Trans id="requests.client-manager.edit-client">Edit client</Trans>}
          />
        </>
      );
    }

    return (
      <Stack row gap="1rem" justifyContent="space-between" alignItems="flex-start">
        <ContactDetails
          type="client"
          name={request.client.name}
          email={request.client.email}
          phone={request.client.phone}
          address={request.client.address}
          placeIcon={null}
          deliveryChoice={request.client.shippingChoice}
          locale={request.client.locale}
          isVIP={request.vip}
        />
        <Stack row gap="0.5rem">
          <Button
            variant="secondary"
            iconOnly
            size="medium"
            onPress={() => setIsEditDrawerOpen(true)}
            ariaLabel={t({
              id: 'requests.client-manager.edit-client',
              message: 'Edit client',
            })}
          >
            <IconEdit />
          </Button>
          <Button
            variant="secondary"
            iconOnly
            size="medium"
            onPress={() =>
              updateRequestClient({
                client: null,
              })
            }
            ariaLabel={t({ id: 'requests.client-manager.remove-client', message: 'Remove client' })}
          >
            <IconCross />
          </Button>
        </Stack>
        <ClientFormDrawer
          isOpen={isEditDrawerOpen}
          setIsOpen={setIsEditDrawerOpen}
          hideDeliveryOption
          customTitle={<Trans id="requests.client-manager.edit-client">Edit client</Trans>}
        />
      </Stack>
    );
  }

  if (isMobile || isTablet) {
    return (
      <>
        <Box padding="0" removeStyleOnMobile removeStyleOnTablet>
          <Stack padding={undefined} alignItems="stretch" gap="0.5rem">
            <Paragraph color="secondary">
              <Trans id="requests.new.client.no-client">No client added yet</Trans>
            </Paragraph>
            <Button
              style={{ flex: 1 }}
              variant="secondary"
              size="medium"
              onPress={() => setIsAddClientModalOpen(true)}
              dataTrackingId="requests.new.client.add"
            >
              <IconAdd />
              <Trans id="requests.new.client.add">Add a client</Trans>
            </Button>
          </Stack>
        </Box>
        <AddClientModal
          isOpen={isAddClientModalOpen}
          onOpenChange={setIsAddClientModalOpen}
          onSelectClient={(client) =>
            updateRequestClient({
              client: {
                ...client,
                dataFromCrm: client,
              },
            })
          }
        />
      </>
    );
  }

  return (
    <ClientSearchInput
      setSelectedClient={(client) =>
        updateRequestClient({
          client: {
            ...client,
            dataFromCrm: client,
          },
        })
      }
    />
  );
};
